// ============================================================
// nameSort.js
// How a list of people and businesses is put in order, in one place.
//
// ONE COMPARATOR, BECAUSE TWO DRIFT. The Customers page and the Documents page
// both order by customer name. Written twice, they would agree on the day they
// were written and disagree the first time someone "fixed" one of them — which
// is exactly how the round-up table ended up duplicated in two editors with
// different numbers in each, and how a 240px sidebar width ended up hard-coded
// in seventeen files.
//
// FULL NAME, AS STORED. No surname detection. "Four Leaf LLC" sorts under F and
// "John Smith" under J, because the name field holds businesses and people side
// by side and no rule can reliably tell which is which: "Smith Fencing" is a
// business, "Grace Kelly" a person, "Kelly Grace Builders" either. A rule that
// guesses wrong moves somebody to a letter nobody would look under.
// ============================================================

// Case-insensitive ("smith" and "Smith" are the same name) and numeric-aware
// ("Fence 2" before "Fence 10", where plain string order puts 10 first because
// "1" < "2"). Built once: constructing a Collator is far more expensive than
// using one, and the Customers page sorts ~9,000 rows.
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

const clean = (v) => String(v ?? '').trim();

/**
 * Compare two names for an A-to-Z sort.
 *
 * A missing, null or blank name sorts LAST rather than throwing or floating to
 * the top. A nameless record is almost always a half-entered one, and putting
 * it first would bury every real name beneath it.
 */
export function compareNames(a, b) {
  const x = clean(a);
  const y = clean(b);
  if (!x && !y) return 0;
  if (!x) return 1;
  if (!y) return -1;
  return collator.compare(x, y);
}

/**
 * How well a name matches a search, as a rank where LOWER IS BETTER:
 *
 *   0  the name starts with the query        "smith"  ->  "Smith Fencing"
 *   1  a word in the name starts with it     "smith"  ->  "Anderson Smith"
 *   2  the query appears anywhere in it      "mit"    ->  "Anderson Smith"
 *   null  the name does not contain it at all
 *
 * Uses the same case-insensitive substring test as the pages' own filters, so
 * anything a filter matched on name gets 0, 1 or 2 — never null. This ORDERS
 * results; it is never used to decide what matches.
 *
 * "A word starts with it" is checked at every occurrence, not just the first,
 * so "smith" in "Blacksmith & Smith" still ranks as a word start.
 */
export function nameMatchRank(name, query) {
  const n = clean(name).toLowerCase();
  const q = clean(query).toLowerCase();
  if (!n || !q) return null;

  let i = n.indexOf(q);
  if (i < 0) return null;
  if (i === 0) return 0;
  while (i >= 0) {
    // A word start is the beginning of the string or a non-alphanumeric before it.
    if (!/[a-z0-9]/.test(n[i - 1])) return 1;
    i = n.indexOf(q, i + 1);
  }
  return 2;
}
